<template>
  <el-dialog :visible.sync="visible" title="添加区块" width="1000px" append-to-body class="modules-modal">
    <div class="modules-modal__body">
      <div class="modules-modal__header">
        <div class="header__title">添加区块</div>
        <i class="el-icon-close header__close" @click="visible = false" />
      </div>
      <!-- 搜索框 -->
      <div class="modules-modal__search">
        <div class="search__wrapper">
          <i class="el-icon-search search__icon"></i>
          <input v-model="searchKeyword" type="text" class="search__input" placeholder="搜索模块名称或描述..."
            @input="handleSearch" />
          <i v-if="searchKeyword" class="el-icon-circle-close search__clear" @click="clearSearch"></i>
        </div>
      </div>
      <!-- 搜索时汇总展示所有分类下的匹配结果 -->
      <div v-if="isSearching">
        <transition-group :key="'search-' + searchKeyword" tag="div" class="group__grid" appear type="animation"
          enter-active-class="animate__animated animate__zoomIn"
          leave-active-class="animate__animated animate__zoomOut">
          <div v-for="(item, idx) in searchResults" :key="'search-' + idx" class="grid__card"
            :style="{ animationDuration: '450ms', animationDelay: (idx * 70) + 'ms' }" @click="handleItemClick(item)">
            <div class="card__cover">
              <img :src="item.pic" alt="" />
            </div>
            <div class="card__meta">
              <div class="meta__title" v-html="renderHighlightedTitle(item)"></div>
              <i v-if="item.icon" class="iconfont meta__icon" :class="item.icon"></i>
            </div>
          </div>
        </transition-group>
        <!-- 搜索无任何结果时的统一空状态 -->
        <div v-if="searchResults.length === 0" class="search-empty">
          <div class="search-empty__icon">
            <i class="el-icon-search"></i>
          </div>
          <div class="search-empty__title">未找到相关模块</div>
          <div class="search-empty__desc">
            尝试使用其他关键词搜索，或者
            <span class="search-empty__clear" @click="clearSearch">清除搜索条件</span>
          </div>
        </div>
      </div>

      <el-tabs v-else v-model="activeTab" class="modules-modal__tabs">
        <el-tab-pane v-for="(group, gIdx) in widgetList" :key="gIdx" :label="group.title" :name="group.title">
          <!-- 有内容时显示卡片列表 -->
          <transition-group v-if="activeTab === group.title && group.children.length > 0" :key="activeTab + '-' + gIdx"
            tag="div" class="group__grid" appear type="animation" enter-active-class="animate__animated animate__zoomIn"
            leave-active-class="animate__animated animate__zoomOut">
            <div v-for="(item, idx) in group.children" :key="group.title + '-' + idx" class="grid__card"
              :style="{ animationDuration: '450ms', animationDelay: (idx * 70) + 'ms' }" @click="handleItemClick(item)">
              <div class="card__cover">
                <img :src="item.pic" alt="" />
              </div>
              <div class="card__meta">
                <div class="meta__title">{{ item.title }}</div>
                <i v-if="item.icon" class="iconfont meta__icon" :class="item.icon"></i>
              </div>
            </div>
          </transition-group>

          <!-- 搜索无结果时的空状态 -->
          <div v-if="activeTab === group.title && group.children.length === 0 && searchKeyword" class="search-empty">
            <div class="search-empty__icon">
              <i class="el-icon-search"></i>
            </div>
            <div class="search-empty__title">未找到相关模块</div>
            <div class="search-empty__desc">
              尝试使用其他关键词搜索，或者
              <span class="search-empty__clear" @click="clearSearch">清除搜索条件</span>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-dialog>
</template>

<script>
import { Dialog, Tabs, TabPane, Message } from 'element-ui'
import cloneDeep from "lodash.clonedeep";
import { insertReduceStep } from "~/utils/event"
import { scrollToWidget } from "~/site/core"
import { DialogCollectionWidgetModel } from '~/site/model/models/DialogCollectionWidgetModel'
export default {
  name: 'ModulesModal',
  components: {
    [Dialog.name]: Dialog,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane
  },
  props: {
    site: Object
  },
  data() {
    return {
      visible: false,
      activeTab: '',
      searchKeyword: '',
      searchTimer: null,
      currentIndex: null
    }
  },
  computed: {
    // 基础分组，避免重复调用 site.getGroupWidgetList
    groupsBase() {
      return this.site?.getGroupWidgetList() || []
    },
    widgetList() {
      const base = this.groupsBase
      const keyword = (this.searchKeyword || '').toLowerCase().trim()
      if (!keyword) return base
      const tokens = this.tokenize(keyword)

      // 返回按关键词过滤后的列表，但不移除分组，以便展示空状态
      return base.map(group => {
        const children = Array.isArray(group.children) ? group.children : []
        const filteredChildren = children.filter(item => this.matchesTokens(item, tokens))
        return { ...group, children: filteredChildren }
      })
    },
    isSearching() {
      return ((this.searchKeyword || '').trim().length > 0)
    },
    searchResults() {
      const base = this.groupsBase
      const keyword = (this.searchKeyword || '').toLowerCase().trim()
      if (!keyword) return []
      const tokens = this.tokenize(keyword)
      const withScore = []
      base.forEach(group => {
        const children = Array.isArray(group.children) ? group.children : []
        children.forEach(item => {
          const score = this.scoreForTokens(item, tokens)
          if (score > 0) withScore.push({ item, score })
        })
      })
      withScore.sort((a, b) => b.score - a.score)
      return withScore.map(e => e.item)
    },
    hasDialogCollect() {
      return Boolean(this.site?.widgetList?.find(item => item.name === DialogCollectionWidgetModel.moduleClass))
    }
  },
  watch: {
    widgetList: {
      immediate: true,
      handler(list) {
        if (Array.isArray(list) && list.length && !this.activeTab) {
          this.activeTab = list[0].title
        }
      }
    }
  },
  methods: {
    init(index) {
      this.visible = true
      this.$nextTick(() => {
        this.currentIndex = typeof index === 'number' ? index : null
        this.activeTab = (this.widgetList[0] && this.widgetList[0].title) || ''
        this.searchKeyword = ''
      })
    },
    escapeHtml(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    escapeRegExp(s) {
      return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    renderHighlightedTitle(item) {
      const raw = (item && item.title) ? String(item.title) : ''
      const safe = this.escapeHtml(raw)
      const tokens = this.tokenize((this.searchKeyword || '').toLowerCase().trim())
      const filtered = Array.from(new Set(tokens.filter(t => t && t.length >= 1)))
      if (filtered.length === 0) return safe
      // 为避免短词覆盖长词，按长度降序
      filtered.sort((a, b) => b.length - a.length)
      const pattern = filtered.map(t => this.escapeRegExp(t)).join('|')
      try {
        return safe.replace(new RegExp(`(${pattern})`, 'gi'), '<span class="hl">$1</span>')
      } catch (e) {
        return safe
      }
    },
    normalizeStr(val) {
      return ((val ?? '') + '').toLowerCase()
    },
    tokenize(keyword) {
      return String(keyword)
        .toLowerCase()
        .split(/\s+/)
        .map(t => t.trim())
        .filter(Boolean)
    },
    // 轻量拼音转换：针对本项目常用中文词汇做短语级替换
    toPinyin(str) {
      const s = this.normalizeStr(str)
      const map = [
        ['分类商品', 'fenlei shangpin'],
        ['分类', 'fenlei'],
        ['商品', 'shangpin'],
        ['列表', 'liebiao'],
        ['样式', 'yangshi'],
        ['单个商品', 'dange shangpin'],
        ['单品', 'danpin'],
        ['详情', 'xiangqing'],
        ['充值', 'chongzhi'],
        ['余额', 'yuee'],
        ['抽奖', 'choujiang'],
        ['签到', 'qiandao'],
        ['邀请码', 'yaoqingma'],
        ['兑换码', 'duihuanma'],
        ['顺序解锁', 'shunxu jiesuo'],
        ['月卡', 'yueka'],
        ['周卡', 'zhouka'],
        ['会员', 'huiyuan'],
        ['分页', 'fenye'],
        ['筛选', 'shaixuan'],
        ['排序', 'paixu'],
        ['网格', 'wangge'],
        ['卡片', 'kapian'],
        ['连续', 'lianxu'],
      ]
      let out = s
      map.forEach(([cn, py]) => {
        out = out.replace(new RegExp(cn, 'g'), py)
      })
      // 去除标点，仅保留字母数字与空格
      out = out.replace(/[^a-z0-9\s]/g, ' ')
      // 合并多余空格
      out = out.replace(/\s+/g, ' ').trim()
      return out
    },
    // 提取搜索候选文本（包含拼音变体）
    getSearchTexts(item) {
      const title = this.normalizeStr(item?.title)
      const desc = this.normalizeStr(item?.desc)
      const keywordsArr = Array.isArray(item?.keywords) ? item.keywords.map(k => this.normalizeStr(k)) : []
      const titlePy = this.toPinyin(title)
      const descPy = this.toPinyin(desc)
      const keywordsPy = keywordsArr.map(k => this.toPinyin(k))
      return { title, desc, keywordsArr, titlePy, descPy, keywordsPy }
    },
    // 简易模糊匹配：子序列或编辑距离阈值
    fuzzyMatch(text, tok) {
      const a = String(text || '')
      const b = String(tok || '')
      if (!a || !b) return false
      // 子序列匹配（顺序包含）
      let i = 0
      for (let j = 0; j < a.length && i < b.length; j++) {
        if (a[j] === b[i]) i++
      }
      if (i === b.length) return true
      // 编辑距离阈值（短词容忍1，长词容忍2）
      const maxDist = b.length >= 5 ? 2 : 1
      const dist = (x, y, threshold) => {
        const m = x.length, n = y.length
        if (Math.abs(m - n) > threshold) return threshold + 1
        const dp = new Array(n + 1)
        for (let j = 0; j <= n; j++) dp[j] = j
        for (let i = 1; i <= m; i++) {
          let prev = i - 1
          dp[0] = i
          for (let j = 1; j <= n; j++) {
            const temp = dp[j]
            dp[j] = Math.min(
              dp[j] + 1,
              dp[j - 1] + 1,
              prev + (x[i - 1] === y[j - 1] ? 0 : 1)
            )
            prev = temp
          }
        }
        return dp[n]
      }
      return dist(a, b, maxDist) <= maxDist
    },
    scoreForTokens(item, tokens) {
      if (!tokens || tokens.length === 0) return 0
      const { title, desc, keywordsArr, titlePy, descPy, keywordsPy } = this.getSearchTexts(item)
      let score = 0
      tokens.forEach(tok => {
        const inTitle = title.includes(tok)
        const inKeywords = keywordsArr.some(k => k.includes(tok))
        const inDesc = desc.includes(tok)
        const inTitlePy = titlePy.includes(tok)
        const inKeywordsPy = keywordsPy.some(k => k.includes(tok))
        const inDescPy = descPy.includes(tok)
        if (inTitle) score += 3
        if (inKeywords) score += 2
        if (inDesc) score += 1
        // 拼音命中权重稍低于原文
        if (inTitlePy) score += 2
        if (inKeywordsPy) score += 2
        if (inDescPy) score += 1
        // 模糊匹配兜底（避免误伤过多，权重低）
        if (!inTitle && !inKeywords && !inDesc && !inTitlePy && !inKeywordsPy && !inDescPy) {
          if (this.fuzzyMatch(title, tok)) score += 1
          else if (keywordsArr.some(k => this.fuzzyMatch(k, tok))) score += 1
          else if (this.fuzzyMatch(desc, tok)) score += 1
        }
      })
      return score
    },
    handleItemClick(item) {
      if (typeof this.currentIndex == 'number') {
        this.addWidgetByIndex(item)
      } else {
        this.handleAdd(item)
      }
    },
    checkAddWidget(item) {
      let result = true
      if (this.hasDialogCollect && item.model.moduleClass === DialogCollectionWidgetModel.moduleClass) {
        result = false
        Message.error('弹窗集模块只可添加一个，请勿重复添加')
      }
      return result
    },
    matchesTokens(item, tokens) {
      return this.scoreForTokens(item, tokens) > 0
    },
    addWidgetByIndex(item) {
      if (!this.checkAddWidget(item)) return
      const index = this.currentIndex === 1 ? 1 : this.currentIndex
      let model
      if (item?.children?.length) {
        model = this.site.addWidgetByIndex(item.children[0].model, index)
      } else {
        model = this.site.addWidgetByIndex(item.model, index)
      }
      this.visible = false
      insertReduceStep(this, { element: cloneDeep(model), index })
      this.$nextTick(() => {
        scrollToWidget(this, model, index)
      })
    },
    handleAdd(item) {
      try {
        const model = new item.model()
        const index = this.site.addWidgetLast(model)
        this.visible = false
        insertReduceStep(this, { element: cloneDeep(model), index })
        this.$store.commit('editor/CLEAR_STORAGE_STEP')
        document.body.click()
        this.$nextTick(() => {
          scrollToWidget(this, model, index)
        })
      } catch (e) {
        console.warn('添加模块失败', e)
      }
    },
    handleSearch() {
      // 防抖搜索，减少高频输入触发的计算
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        const keyword = (this.searchKeyword || '').toLowerCase().trim()
        // 计算属性 widgetList 会根据关键词自动过滤，这里只确保 activeTab 指向有结果的分组
        const current = (this.widgetList || []).find(g => g.title === this.activeTab)
        const hasCurrent = current && Array.isArray(current.children) && current.children.length > 0
        if (!keyword) {
          // 关键词为空时，回到第一个分组
          if ((this.widgetList || []).length) this.activeTab = this.widgetList[0].title
          return
        }
        if (!hasCurrent) {
          const firstWithResult = (this.widgetList || []).find(g => (Array.isArray(g.children) ? g.children.length : 0) > 0)
          if (firstWithResult) this.activeTab = firstWithResult.title
        }
      }, 150)
    },
    clearSearch() {
      this.searchKeyword = ''
      this.handleSearch()
    }
  }
}
</script>

<style scoped lang="less">
.modules-modal {
  /deep/ .el-dialog {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    background: linear-gradient(180deg, #ffffff 0%, #f7f9ff 100%);
    border: 1px solid rgba(22, 119, 255, 0.10);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
  }

  /deep/ .el-dialog::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(800px 200px at 50% 0, rgba(22, 119, 255, 0.08), rgba(22, 119, 255, 0) 70%);
  }

  /deep/ .el-dialog__body {
    padding: 16px;
    background: transparent;
  }

  /deep/ .el-dialog__header {
    display: none;
  }
}

.modules-modal__header {
  position: relative;
  padding: 0;
}

.header__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  line-height: 30px;
  text-align: center;
}

.header__close {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  font-size: 18px;
  color: #666;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08), inset 0 1px rgba(255, 255, 255, 0.6);
  backdrop-filter: saturate(180%) blur(6px);
  transition: background .2s ease, box-shadow .2s ease, color .2s ease, transform .15s ease;
}

.modules-modal__search {
  margin: 20px 0 16px;
  padding: 0 4px;
}

.search__wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.85);
  border: 1.5px solid rgba(22, 119, 255, 0.12);
  border-radius: 16px;
  padding: 0 16px;
  height: 48px;
  box-shadow:
    0 4px 16px rgba(22, 119, 255, 0.08),
    0 1px 4px rgba(0, 0, 0, 0.04),
    inset 0 1px rgba(255, 255, 255, 0.8);
  backdrop-filter: saturate(150%) blur(8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.search__wrapper::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg,
      rgba(22, 119, 255, 0.02) 0%,
      rgba(22, 119, 255, 0.06) 50%,
      rgba(22, 119, 255, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.search__wrapper:hover {
  border-color: rgba(22, 119, 255, 0.2);
  box-shadow:
    0 6px 24px rgba(22, 119, 255, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.06),
    inset 0 1px rgba(255, 255, 255, 0.9);
  transform: translateY(-1px);
}

.search__wrapper:hover::before {
  opacity: 1;
}

.search__wrapper:focus-within {
  border-color: rgba(22, 119, 255, 0.4);
  box-shadow:
    0 8px 32px rgba(22, 119, 255, 0.16),
    0 0 0 4px rgba(22, 119, 255, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.06),
    inset 0 1px rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
}

.search__wrapper:focus-within::before {
  opacity: 1;
}

.search__icon {
  font-size: 18px;
  color: rgba(22, 119, 255, 0.6);
  margin-right: 12px;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.search__wrapper:focus-within .search__icon {
  color: rgba(22, 119, 255, 0.8);
  transform: scale(1.1);
}

.search__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: #333;
  line-height: 1.4;
  font-weight: 400;
  letter-spacing: 0.2px;

  &::placeholder {
    color: rgba(102, 102, 102, 0.6);
    font-weight: 400;
    transition: color 0.3s ease;
  }

  &:focus::placeholder {
    color: rgba(22, 119, 255, 0.4);
  }
}

.search__clear {
  font-size: 16px;
  color: rgba(102, 102, 102, 0.5);
  margin-left: 8px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    color: rgba(22, 119, 255, 0.7);
    background: rgba(22, 119, 255, 0.08);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
}

.search-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  min-height: 300px;
}

.search-empty__icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(22, 119, 255, 0.08) 0%, rgba(22, 119, 255, 0.16) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(22, 119, 255, 0.2), rgba(22, 119, 255, 0.05));
    z-index: -1;
  }

  i {
    font-size: 32px;
    color: rgba(22, 119, 255, 0.6);
  }
}

.search-empty__title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  letter-spacing: 0.2px;
}

.search-empty__desc {
  font-size: 14px;
  color: rgba(102, 102, 102, 0.8);
  line-height: 1.5;
  max-width: 300px;
}

.search-empty__clear {
  color: rgba(22, 119, 255, 0.8);
  cursor: pointer;
  font-weight: 500;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    color: rgba(22, 119, 255, 1);
    border-bottom-color: rgba(22, 119, 255, 0.3);
  }
}

.header__close:hover {
  color: @primary-color;
  background: rgba(22, 119, 255, 0.10);
  box-shadow: 0 10px 24px rgba(22, 119, 255, 0.18);
}

.header__close:active {
  transform: translateY(-50%) scale(0.98);
}

.modules-modal__tabs {
  margin: 0 0 12px;
}

// /* 优雅 Tab 样式 */
/deep/ .el-tabs__nav-wrap::after {
  height: 0;
}

/deep/ .el-tabs__item:hover {
  color: @primary-color !important;
}

/deep/ .el-tabs__item.is-active {
  color: @primary-color !important;
}

/deep/ .el-tabs__active-bar {
  background-color: @primary-color;
}

.group__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  padding-top: 5px;
}

.grid__card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow .2s ease, transform .2s ease, border-color .2s ease;
  will-change: transform;

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: @primary-color;
    transform: translateY(-2px);
  }
}

.card__cover {
  position: relative;
  width: 100%;
  height: 140px;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}


.card__cover img {
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
  transition: transform .25s ease;
}

.grid__card:hover .card__cover img {
  transform: scale(1.06);
}

.card__cover::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(22, 119, 255, 0.10), rgba(22, 119, 255, 0));
  opacity: 0;
  transition: opacity .2s ease;
}

.grid__card:hover .card__cover::after {
  opacity: 1;
}

.card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
}

.meta__title {
  font-size: 14px;
  color: #333;
}

/deep/ .meta__title .hl {
  background: rgba(22, 119, 255, 0.12);
  color: #174ea6;
  padding: 0 2px;
  border-radius: 3px;
  box-shadow: inset 0 -1px rgba(22, 119, 255, 0.25);
}

.meta__icon {
  text-align: center;
  font-size: 18px;
  color: white;
  width: 30px;
  line-height: 30px;
  border-radius: 100%;
  background-color: #3D46F5;
}
</style>